import React from "react";
import { GET_ONE, Link } from "react-admin";
import { Card, CardContent, CardHeader } from "@material-ui/core";
import dataProviderFactory from "../dataProvider";
import moment from "moment";
import styled from "styled-components";
import {
  prop,
  groupBy,
  map,
  curry,
  isNil,
  converge,
  findIndex,
  length,
  unnest,
  path,
  sortBy,
  aperture,
  range,
  last,
  concat,
  mapAccum,
  when,
  complement,
  addIndex
} from "ramda";
import LinearProgress from "@material-ui/core/LinearProgress";

const StyledLink = styled(Link)`
  :link {
    color: #000000;
    text-decoration: underline;
  }
  :visited {
    color: #000000;
  }
  :hover {
    color: #000000;
  }
  :active {
    color: #000000;
  }
`;

const ColumnLeft = styled.div(props => ({
  display: "flex",
  flexDirection: "row",
  width: "11rem",
  backgroundColor: props.color,
  border: "1px solid #ffffff",
  padding: "4px"
}));

const ColumnLeftNoFlex = styled.div(props => ({
  display: "flex",
  flexDirection: "column",
  width: "11rem",
  backgroundColor: props.color,
  border: "1px solid #ffffff",
  padding: "4px"
}));

const Column = styled.div(props => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: props.color,
  border: "1px solid #ffffff",
  width: "25rem",
  padding: "4px"
}));

const WeekDay = styled.div({
  width: "6.5rem",
  padding: "4px"
});
const Date = styled.div({
  width: "7rem",
  padding: "4px"
});

const Row = styled.div({
  display: "flex"
});

class VehicleHistory extends React.Component {
  async fetchData(id) {
    const dataProvider = await dataProviderFactory("rest");
    const response = await dataProvider(GET_ONE, "vehicles", { id });

    return response.data;
  }

  async componentDidMount() {
    const id = this.props.match.params.id;
    await this.updateState(id);
  }

  updateState = async id => {
    const data = await this.fetchData(id);
    this.setState({ data });
  };

  /**
   * Normaliza fechas de evento a día de calendario.
   * Evita gaps/off-by-one al mezclar DATEONLY ("YYYY-MM-DD") con
   * timestamps UTC a medianoche ("...T00:00:00.000Z"), porque moment.diff('days')
   * trunca por ms exactos y no por días civiles.
   */
  calendarDay = date => {
    if (isNil(date)) {
      return null;
    }
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return moment(date).startOf("day");
    }
    if (
      typeof date === "string" &&
      /T00:00:00(\.\d+)?Z$/.test(date)
    ) {
      return moment(moment.utc(date).format("YYYY-MM-DD")).startOf("day");
    }
    return moment(date).startOf("day");
  };

  sortedDayList = (start, end, nextEventDate) => {
    const startDay = start.clone().startOf("day");
    const endDay = end.clone().startOf("day");
    const nextDay = nextEventDate ? this.calendarDay(nextEventDate) : null;
    const add = (day, offset) => day.clone().add(offset, "days");

    if (nextDay && endDay.isSame(nextDay, "day")) {
      return map(curry(add)(startDay), range(0, endDay.diff(startDay, "days")));
    }
    return map(
      curry(add)(startDay),
      range(0, endDay.diff(startDay, "days") + 1)
    );
  };

  blockForHoldEvent = (hold, nextEventDate) => {
    const start = this.calendarDay(hold.start_date);
    const end = this.calendarDay(hold.end_date);

    const days = this.sortedDayList(start, end, nextEventDate);

    return map(
      day => (
        <Row
          id={`${day.format("YYYY_MM_DD")}_${hold.id}`}
          key={`${day.format("YYYY_MM_DD")}_${hold.id}`}
        >
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#FF00CC"}>
            Hold: &nbsp;{" "}
            <StyledLink to={{ pathname: `/holds/${hold.id}` }}>
              {hold.id}
            </StyledLink>
          </ColumnLeft>
          <Column color={"#FF00CC"}>{hold.comment}</Column>
        </Row>
      ),
      days
    );
  };

  maintItemL = maintenances => {
    return map(
      maintenance => (
        <React.Fragment key={maintenance.event.id}>
          <p>
            Maintenance:{" "}
            <StyledLink
              to={{ pathname: `/maintenances/${maintenance.event.id}` }}
            >
              {maintenance.event.id}
            </StyledLink>
          </p>
        </React.Fragment>
      ),
      maintenances
    );
  };

  maintItemLdesc = maintenances => {
    return map(
      maintenance => (
        <React.Fragment key={`id_${maintenance.event.id}`}>
          <p>
            {
              maintenance.event.maintenance_item.maintenance_item_type
                .description
            }{" "}
            ({maintenance.event.maintenance_item.description}
            {maintenance.event.maintenance_item.rev_kms
              ? ` - ${maintenance.event.maintenance_item.rev_kms} kms`
              : ""}
            ){" "}
            {maintenance.event.maintenance_kms ? (
              <b>{` ${maintenance.event.maintenance_kms} kms`}</b>
            ) : (
              ""
            )}
          </p>
        </React.Fragment>
      ),
      maintenances
    );
  };

  maintComp = (date, events) => {
    const bgColor = isNil(events[0].event.location)
      ? "whitesmoke"
      : events[0].event.location.color;
    return (
      <Row id={1} key={1}>
        <WeekDay>{moment(date).format("dddd")}</WeekDay>
        <Date>{moment(date).format("DD-MM-YYYY")}</Date>
        <ColumnLeftNoFlex color={bgColor}>
          {this.maintItemL(events)}
        </ColumnLeftNoFlex>
        <Column color={bgColor}>{this.maintItemLdesc(events)}</Column>
      </Row>
    );
  };

  damageItemL = damages => {
    return map(
      damage => (
        <React.Fragment key={damage.event.id}>
          <p>
            Damage:{" "}
            <StyledLink
              to={{ pathname: `/vehicle_damages/${damage.event.id}` }}
            >
              {damage.event.id}
            </StyledLink>
          </p>
        </React.Fragment>
      ),
      damages
    );
  };

  damageItemLdesc = damages => {
    return map(
      damage => (
        <React.Fragment key={`di_${damage.event.id}`}>
          <p>{damage.event.comment}</p>
        </React.Fragment>
      ),
      damages
    );
  };

  damageComp = (date, events) => {
    const bgColor = isNil(events[0].event.location)
      ? "whitesmoke"
      : events[0].event.location.color;
    return (
      <Row id={1}>
        <WeekDay>{moment(date).format("dddd")}</WeekDay>
        <Date>{moment(date).format("DD-MM-YYYY")}</Date>
        <ColumnLeftNoFlex color={bgColor}>
          {this.damageItemL(events)}
        </ColumnLeftNoFlex>
        <Column color={bgColor}>{this.damageItemLdesc(events)}</Column>
      </Row>
    );
  };

  buildLinkForTour = (tour, title) => {
    return (
      <StyledLink
        target={"_blank"}
        to={{
          pathname: `/tours/${tour.id}`
        }}
      >
        {title}
      </StyledLink>
    );
  };

  buildLinkTourRental = (rental, title) => {
    return (
      <StyledLink
        to={{
          pathname: `/${rental.type === 2 ? "process_rental_edit" : "tour_clients"
            }/${rental.type === 2 ? rental.id : `${rental.id}/show`}`
        }}
      >
        {title}
      </StyledLink>
    );
  };

  buildTitleTourRental = (rental, color) => {
    return (
      <ColumnLeft color={rental.type === 2 ? color : "#CC9999"}>
        {" "}
        {rental.type === 2 ? "Rental" : "Tour"}: &nbsp;{" "}
        {this.buildLinkTourRental(
          rental,
          rental.id
          // rental.type === 2 ? rental.id : rental.booking.tour_id
        )}
      </ColumnLeft>
    );
  };

  blockForTour = (tour, nextEventDate) => {
    const start = this.calendarDay(tour.effective_start_date);
    const end = this.calendarDay(tour.effective_end_date);
    let days = this.sortedDayList(start, end, nextEventDate);

    const tourBike = tour.TourBike

    return addIndex(map)((day, i) => {

      const extraInfo = (() => {
        if (i === 0) {
          return <Column>{tourBike.kms_1} km</Column>;
        }
        if (i === length(days) - 1) {
          return <Column>{tourBike.kms_2} km</Column>;
        }
        return null;
      })();

      return (
        <Row id={tour.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#66CCFF"}>
            {this.state.data.licence_plate} {this.state.data.renting ? "®" : ""}
          </ColumnLeft>
          <Column color={"#66CCFF"}>
            {this.buildLinkForTour(
              tour,
              ` Guide Bike - ${path(["tour_name", "tour_name_en"], tour)} (${tour.id
              })`
            )}
          </Column>
          {extraInfo}
        </Row>
      );
    }, days);
  };

  blockForRental = (rental, nextEventDate) => {
    const start = this.calendarDay(rental.date_1);
    const end = this.calendarDay(rental.date_2);

    let days = this.sortedDayList(start, end, nextEventDate);

    return addIndex(map)((day, i) => {
      const color = (() => {
        if (
          (i === 0 && rental.time_1 === "18:00:00") ||
          (i === length(days) - 1 && rental.time_2 === "10:00:00")
        ) {
          return "#FFCC66";
        }
        return "red";
      })();
      const extraInfo = (() => {
        if (i === 0) {
          if (length(days) - 1 === 0) {
            return <Column>{rental.km_2} km</Column>;
          }
          return <Column>{rental.km_1} km</Column>;
        }
        if (i === length(days) - 1) {
          return <Column>{rental.km_2} km</Column>;
        }

        return null;
      })();

      return (
        <Row id={rental.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          {this.buildTitleTourRental(rental, color)}
          <Column
            color={rental.type === 2 ? color : "#CC9999"}
          >{`${rental.booking.r_first_name} ${rental.booking.r_last_name}`}</Column>
          {extraInfo}
        </Row>
      );
    }, days);
  };

  blockForTransfer = (transfer, nextEventDate) => {
    const start = this.calendarDay(transfer.transfer_date);
    const end = this.calendarDay(transfer.setup_date);
    const days = this.sortedDayList(start, end, nextEventDate);

    return map(
      day => (
        <Row id={transfer.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#9966FF"}>
            Transf: &nbsp;{" "}
            <StyledLink to={{ pathname: `/transfers/${transfer.id}` }}>
              {transfer.id}
            </StyledLink>
          </ColumnLeft>
          <Column color={"#9966FF"}>{transfer.TransferItem.notes}</Column>
        </Row>
      ),
      days
    );
  };

  blockForTourVans = (tour, nextEventDate) => {
    const start = this.calendarDay(tour.effective_start_date);
    const end = this.calendarDay(tour.effective_end_date);
    let days = this.sortedDayList(start, end, nextEventDate);

  const tourVans = tour.tour_vans
    return addIndex(map)((day, i) => {
      
      const extraInfo = (() => {
        if (i === 0) {
          return <Column>{tourVans != null ? tourVans[0].kms_1 : 0} km</Column>;
        }
        if (i === length(days) - 1) {
          return <Column>{tourVans != null ? tourVans[0].km_2 : 0} km</Column>;
        }
        return null;
      })();

      return (
        <Row id={tour.id}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={"#CC9999"}>
            {this.state.data.licence_plate} {this.state.data.renting ? "®" : ""}
          </ColumnLeft>
          <Column color={"#CC9999"}>
            {this.buildLinkForTour(
              tour,
              ` Tour Vans - ${path(["tour_name", "tour_name_en"], tour)} (${tour.id
              })`
            )}
          </Column>
          {extraInfo}
        </Row>
      );
    }, days);
  };

  daysForEvent = ({ event, type }, nextEventDate) => {
   // console.log('%chistory.js line:376 object', 'color: #007acc;', type);
    switch (type) {
      case "hold":
        return this.blockForHoldEvent(event, nextEventDate);
      case "transfer":
        return this.blockForTransfer(event, nextEventDate);
      case "rental":
        return this.blockForRental(event, nextEventDate);
      case "tour":
        return this.blockForTour(event, nextEventDate);
      case "tour_vans":
        return this.blockForTourVans(event, nextEventDate);
      default:
        return this.blockForRental(event, nextEventDate);
    }
  };

  locationForEvent = ({ event, type }) => {
  // console.log(event)
    switch (type) {
      case "hold":
        return null;
      case "transfer":
        return event.location_2;
      case "rental":
        return event.location_2;
      case "tour_vans":
        return event.location_2;
      default:
        return event.location_2;
    }
  };

  bikeAtBase = (prevEventDate, nextEventDate, location) => {
    if (nextEventDate === null) {
      return [];
    }

    const start = this.calendarDay(prevEventDate).add(1, "day");
    const end = this.calendarDay(nextEventDate).add(-1, "day");

    const days = this.sortedDayList(start, end);

    return map(
      curry((vehicle, day) => (
        <Row id={`v_${vehicle.id}_${day}`}>
          <WeekDay>{day.format("dddd")}</WeekDay>
          <Date>{day.format("DD-MM-YYYY")}</Date>
          <ColumnLeft color={location.color}>
            {vehicle.licence_plate} {vehicle.renting ? "®" : ""}
          </ColumnLeft>
          <Column color={location.color}>{location.location_name}</Column>
        </Row>
      ))(this.state.data),
      days
    );
  };

  render() {
    if (this.state === null) {
      return (
        <Card>
          <LinearProgress />
        </Card>
      );
    }

    // const getInArray = chain(values)
    // const descsArray = (elements) =>  map(compose(prop('description'), prop('event')),elements).join(",")

    const buildEvents = (events, type, start, end) =>
      map(event => ({ type, event, start, end }), events);
    const startDate = converge(prop, [prop("start"), prop("event")]);
    const endDate = converge(prop, [prop("end"), prop("event")]);

    const tours = buildEvents(
      this.state.data.tours,
      "tour",
      "effective_start_date",
      "effective_end_date"
    );

    const transfers = buildEvents(
      this.state.data.transfers,
      "transfer",
      "transfer_date",
      "setup_date"
    );
    const rentals = buildEvents(
      this.state.data.rentals,
      "rental",
      "date_1",
      "date_2"
    );
    const holds = buildEvents(
      this.state.data.holds,
      "hold",
      "start_date",
      "end_date"
    );

    const maintenances = buildEvents(
      this.state.data.maintenances,
      "maintenance",
      "maintenance_date",
      "maintenance_date"
    );
    const vehicleDamages = buildEvents(
      this.state.data.vehicle_damages,
      "vehicle_damage",
      "damage_date",
      "damage_date"
    );

    const toursVans = buildEvents(
      this.state.data.vehicleVans,
      "tour_vans",
      "effective_start_date",
      "effective_end_date"
    );

    const groupedMaintenances = groupBy(startDate, maintenances);
    const groupedvehicleDamages = groupBy(startDate, vehicleDamages);

    const events = unnest([transfers, rentals, holds, tours, toursVans]);
    const sortedEvents = sortBy(startDate, events);

    const [location, eventsBlocks] = mapAccum(
      (location, [event, next]) => {
        const nextEventDate = when(complement(isNil))(startDate)(next);
        const lastLocation = this.locationForEvent(event) || location;

        return [
          lastLocation,
          concat(
            this.daysForEvent(event, nextEventDate),
            this.bikeAtBase(endDate(event), nextEventDate, lastLocation || {})
          )
        ];
      },
      null,
      concat(aperture(2, sortedEvents), [[last(sortedEvents), null]])
    );

    const t = unnest(eventsBlocks);

    const updateAtDate = (maintEvents, mainComponent) => {
      const mainKeys = Object.keys(maintEvents);

      mainKeys.map(maintEventDate => {
        const formatedDate = moment(maintEventDate).format("DD-MM-YYYY");
        const idx = findIndex(item => {
          return item.props.children[1].props.children === formatedDate;
        }, mainComponent);
        const comp = this.maintComp(
          maintEventDate,
          maintEvents[maintEventDate]
        );
        mainComponent.splice(idx, 0, comp);
        return mainComponent;
      });

      return mainComponent;
    };

    const updateDamageAtDate = (damageEvents, damageComponent) => {
      const damKeys = Object.keys(damageEvents);

      damKeys.map(damageEventDate => {
        const formatedDate = moment(damageEventDate).format("DD-MM-YYYY");
        const idx = findIndex(item => {
          return item.props.children[1].props.children === formatedDate;
        }, damageComponent);
        const comp = this.damageComp(
          damageEventDate,
          damageEvents[damageEventDate]
        );
        damageComponent.splice(idx, 0, comp);
        return damageComponent;
      });

      return damageComponent;
    };

    const data = updateAtDate(groupedMaintenances, t);
    const finalData = updateDamageAtDate(groupedvehicleDamages, data);
    const boughtAtLocation =
      path(["data", "base_location", "location_name"], this.state) || null;
    const strPurchasedAt = !isNil(boughtAtLocation)
      ? ` - Purchased at: ${boughtAtLocation}`
      : "";

    return (
      <Card>
        <CardHeader
          title={`Vehicle History ${this.state.data.licence_plate} ${this.state.data.renting ? "®" : ""} ${strPurchasedAt}`}
        />
        <CardContent>{finalData}</CardContent>
      </Card>
    );
  }
}

export default VehicleHistory;



// WEBPACK FOOTER //
// ./src/components/history.js